"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { decodeJwtPayload, isTokenExpired } from "@/lib/auth";
import { ACCESS_COOKIE } from "@/lib/cookies";

interface AuthUser {
  id: string;
  email: string;
  display_name: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: async () => {},
  refresh: async () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user from cookie
  const loadUser = useCallback(() => {
    if (typeof document === "undefined") return;

    const cookie = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${ACCESS_COOKIE}=`));
    const token = cookie?.split("=").slice(1).join("=");

    if (!token || isTokenExpired(token, 0)) {
      setUser(null);
      setLoading(false);
      return;
    }

    const payload = decodeJwtPayload(token);
    if (payload && payload.typ === "passenger") {
      setUser({
        id: payload.sub as string,
        email: payload.email as string,
        display_name: payload.display_name as string,
      });
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  // Refresh token
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/auth/refresh", { method: "POST" });
      if (!res.ok) return false;
      loadUser();
      return true;
    } catch {
      return false;
    }
  }, [loadUser]);

  // Logout
  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    router.push("/login");
  }, [router]);

  // Init
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Auto-refresh before expiry
  useEffect(() => {
    if (!user) return;

    const cookie = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${ACCESS_COOKIE}=`));
    const token = cookie?.split("=").slice(1).join("=");
    if (!token) return;

    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") return;

    // Refresh 1 minute before expiry
    const msUntilRefresh = (payload.exp as number) * 1000 - Date.now() - 60000;
    if (msUntilRefresh <= 0) {
      refresh();
      return;
    }

    const timer = setTimeout(() => {
      refresh();
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [user, refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
